/**
 * route() çağrısının parametrelerini Ziggy'nin beklediği adlı nesneye çevirir (app.js'teki genel sarmalayıcı kullanır).
 * Sınama: tests/js/route-params.check.mjs (node ile çalışır).
 */

/**
 * Dizi olarak verilen parametreleri rotanın parametre sırasına göre adlandırır:
 * route('projects.estimates.import', [104, 4]) → { projectId: 104, estimateId: 4 }.
 */
export function namedRouteParams(params, parameterNames) {
  const names = parameterNames.filter(name => name !== 'tenant_slug')

  // Eksik parametre adlandırılmaz; hangisinin eksik olduğunu Ziggy söyler
  return Object.fromEntries(names.map((name, i) => [name, params[i]]).filter(([, value]) => value !== undefined))
}

/**
 * Parametreleri adlı nesneye çevirir ve tenant_slug ekler.
 * routes: Ziggy rota listesi (Ziggy.routes). Liste ya da rota yoksa ada bakılarak tahmin edilir (eski davranış).
 */
export function normalizeRouteParams(name, params, tenantSlug, routes) {
  // Case 1: No params provided - create object with just tenant_slug
  if (params === undefined || params === null) {
    params = { tenant_slug: tenantSlug }
  }
  // Case 2: Primitive param (number/string) or array - convert to object
  else if (typeof params !== 'object' || Array.isArray(params)) {
    const routeConfig = routes?.[name]

    if (routeConfig?.parameters) {
      // Filter out tenant_slug to find the actual resource parameters
      const nonTenantParams = routeConfig.parameters.filter(p => p !== 'tenant_slug')

      if (Array.isArray(params)) {
        params = namedRouteParams(params, routeConfig.parameters)
      } else if (nonTenantParams.length === 1) {
        // Single param: use it directly (e.g., roleId, project, employee)
        params = { [nonTenantParams[0]]: params }
      } else if (nonTenantParams.length === 0) {
        // No params other than tenant_slug (e.g., tenant.dashboard)
        params = {}
      } else {
        // Multiple params: fallback to 'id' (shouldn't happen with primitive param)
        params = { id: params }
      }
    } else {
      // Fallback: Ziggy config not available, use singularization logic
      const parts = name.split('.')
      if (parts.length === 2) {
        const resourcePlural = parts[0]

        // Singularize the resource name
        // Handle hyphenated resource names (e.g., "inspection-companies" → "inspectionCompany")
        let paramName
        if (resourcePlural.includes('-')) {
          // Split by hyphen, singularize the last part, then convert to camelCase
          const hyphenParts = resourcePlural.split('-')
          const lastPart = hyphenParts[hyphenParts.length - 1]
          const singularLastPart = lastPart.endsWith('ies')
            ? lastPart.slice(0, -3) + 'y'  // companies → company
            : lastPart.endsWith('s') && lastPart.length > 1
              ? lastPart.slice(0, -1)       // users → user
              : lastPart
          hyphenParts[hyphenParts.length - 1] = singularLastPart

          // Convert hyphenated name to camelCase: "inspection-company" → "inspectionCompany"
          paramName = hyphenParts[0] + hyphenParts.slice(1).map(part =>
            part.charAt(0).toUpperCase() + part.slice(1)
          ).join('')
        } else {
          // Simple singularization for non-hyphenated names
          paramName = resourcePlural.endsWith('ies')
            ? resourcePlural.slice(0, -3) + 'y'  // categories → category
            : resourcePlural.endsWith('s') && resourcePlural.length > 1
              ? resourcePlural.slice(0, -1)       // contracts → contract
              : resourcePlural
        }

        // Add 'Id' suffix to match route parameter naming convention (e.g., shiftId, inspectionId)
        params = { [paramName + 'Id']: params }
      } else {
        // Fallback for complex route names: use 'id'
        params = { id: params }
      }
    }
  }

  // Case 3: Object params - add tenant_slug if not present
  // This must come AFTER Case 2 to add tenant_slug to all param objects
  if (typeof params === 'object' && !Array.isArray(params)) {
    // Remove legacy 'tenant' parameter to avoid query string pollution
    // Old code used 'tenant:', new code uses 'tenant_slug:' via subdomain routing
    if ('tenant' in params) {
      delete params.tenant
    }
    if ('tenantId' in params) {
      delete params.tenantId
    }
    if (tenantSlug && !params.tenant_slug) {
      params = { tenant_slug: tenantSlug, ...params }
    }
    params = renameMismatchedKeys(params, routes?.[name])
  }

  return params
}

/**
 * Rotanın zorunlu parametresi yoksa ve yerine benzer adlı anahtar verilmişse o ada taşır: { leaveRequest: 5 } → { leaveRequestId: 5 },
 * { constructionPermit: 5 } → { permitId: 5 }. Eksik parametre Ziggy'de hata verip adresi '#' yapıyordu (30.10: 102 çağrı).
 * Bütün parametreleri verilmiş çağrıya dokunmaz; benzer ad yoksa tek eksik + tek fazla anahtar eşlenir.
 */
export function renameMismatchedKeys(params, routeConfig) {
  const names = (routeConfig?.parameters || []).filter(p => p !== 'tenant_slug')
  const missing = names.filter(p => !(p in params) && !routeConfig.uri?.includes(`{${p}?}`))
  if (!missing.length) return params

  const extra = Object.keys(params).filter(k => k !== 'tenant_slug' && !names.includes(k))
  const base = s => s.toLowerCase().replace(/id$/, '')
  const similar = (k, p) => base(k) !== '' && (base(k).endsWith(base(p)) || base(p).endsWith(base(k)))
  const single = missing.length === 1 && extra.length === 1
  const out = { ...params }
  for (const p of missing) {
    const k = extra.find(k => similar(k, p)) ?? (single ? extra[0] : undefined)
    if (k === undefined) continue
    out[p] = out[k]
    delete out[k]
    extra.splice(extra.indexOf(k), 1)
  }
  return out
}
